/**
 * Reusable melody encoder.
 *
 * Encode a tune ONCE as a sequence of {interval, beats}, where `interval`
 * is the number of semitones above the tonic (positive = up, negative =
 * down — use this to write octaves explicitly so the tune doesn't wrap
 * when transposed to a higher key).
 *
 * Then derive both the audio sequence and the VexFlow notation from the
 * same source so the score and the playback can never drift apart.
 */

export interface MelodyNote {
  /** Semitones above the tonic. May exceed an octave. Negative = below. */
  interval: number;
  /** Length in quarter-note beats. 1 = quarter, 2 = half, 4 = whole, 0.5 = eighth, 0.25 = sixteenth. */
  beats: number;
  /** Optional rest. When true, no note plays but the duration consumes time. */
  rest?: boolean;
  /** Optional tie-out (held into the next note). For now informational. */
  tied?: boolean;
}

export interface MelodyLine {
  label?: string;
  notes: MelodyNote[];
}

import { spellPc, toAscii } from "@/theory/spell";

export function midiToPitchName(midi: number, useFlats = false): string {
  const pc = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${toAscii(spellPc(pc, useFlats, true))}${oct}`;
}

/** VexFlow uses lowercase letter, optional accidental, slash, octave: "c#/4". */
export function midiToVexKey(midi: number, useFlats = false): string {
  const pc = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${toAscii(spellPc(pc, useFlats, true)).toLowerCase()}/${oct}`;
}

/**
 * The note values we can actually draw, longest first, as [beats, vex, tone].
 *
 * These conversions used to be `>=` ladders, so 2.5 beats rendered AND played
 * as a plain half note while the bar counter advanced the full 2.5 — the score
 * and the audio drifted apart silently. Now an unrepresentable value is an
 * explicit, visible problem rather than a quiet wrong answer.
 */
const NOTE_VALUES: [beats: number, vex: string, tone: string][] = [
  [4,    "w",   "1n"],
  [3,    "hd",  "2n."],
  [2,    "h",   "2n"],
  [1.5,  "qd",  "4n."],
  [1,    "q",   "4n"],
  [0.75, "8d",  "8n."],
  [0.5,  "8",   "8n"],
  [0.375,"16d", "16n."],
  [0.25, "16",  "16n"],
  [0.125,"32",  "32n"],
];

const EPS = 1e-6;

/** The exact entry for a beat count, or null when no single note value fits. */
function exactValue(beats: number) {
  return NOTE_VALUES.find(([b]) => Math.abs(b - beats) < EPS) ?? null;
}

/** Nearest representable value — used only as a last resort, and warned about. */
function nearestValue(beats: number) {
  let best = NOTE_VALUES[NOTE_VALUES.length - 1];
  let bestGap = Infinity;
  for (const v of NOTE_VALUES) {
    const gap = Math.abs(v[0] - beats);
    if (gap < bestGap) { bestGap = gap; best = v; }
  }
  if (import.meta.env?.DEV) {
    console.warn(
      `[melody] ${beats} beats is not a writable note value; drawing/playing ` +
      `${best[0]} instead. Split it into tied notes so the score and the audio agree.`
    );
  }
  return best;
}

const valueFor = (beats: number) => exactValue(beats) ?? nearestValue(beats);

/** Can this duration be written as a single (optionally dotted) note? */
export function isWritableDuration(beats: number): boolean {
  return exactValue(beats) !== null;
}

/** Convert beat count to a VexFlow duration code. */
export function beatsToVexDuration(beats: number): string {
  return valueFor(beats)[1];
}

/** Convert beat count to a Tone.js duration string. */
export function beatsToToneDuration(beats: number): string {
  return valueFor(beats)[2];
}

export interface AudioEvent {
  time: number;
  notes: string[];
  duration: string | number;
  velocity?: number;
}

/**
 * Convert a flat melody to a Tone.js audio sequence, in seconds at the
 * given BPM. The tonicMidi parameter is the absolute MIDI number of degree 1
 * — e.g. 60 = middle C — so the melody plays in the correct octave.
 */
export function melodyToSequence(
  melody: MelodyNote[],
  tonicMidi: number,
  bpm: number,
  useFlats = false
): AudioEvent[] {
  const secPerBeat = 60 / bpm;
  let time = 0;
  const out: AudioEvent[] = [];
  for (const n of melody) {
    if (!n.rest) {
      out.push({
        time,
        notes: [midiToPitchName(tonicMidi + n.interval, useFlats)],
        duration: beatsToToneDuration(n.beats),
        velocity: 0.85,
      });
    }
    time += n.beats * secPerBeat;
  }
  return out;
}

/** A measure ready for the existing <Notation /> component. */
export interface NotationMeasure {
  chords: { keys: string[]; duration?: string }[];
  label?: string;
}

/**
 * Split a melody into measures of a fixed beat-length (4 for 4/4, 3 for
 * 3/4, etc.) and emit VexFlow-friendly chords. Notes that would cross
 * a barline are split with a tie (currently kept simple — we assume the
 * encoded melody is already barline-respecting; Twinkle is).
 */
export function melodyToMeasures(
  lines: MelodyLine[],
  tonicMidi: number,
  beatsPerBar = 4,
  useFlats = false
): NotationMeasure[] {
  const measures: NotationMeasure[] = [];
  let current: NotationMeasure = { chords: [] };
  let beats = 0;
  let firstLineNote = true;
  let pendingLabel: string | undefined;

  for (const line of lines) {
    if (line.label) {
      pendingLabel = line.label;
      firstLineNote = true;
    }
    for (const n of line.notes) {
      // A rest suffix goes AFTER the dots — "hdr", not "hrd" — and the
      // renderer strips the dots before handing the base value to VexFlow.
      const dur = beatsToVexDuration(n.beats) + (n.rest ? "r" : "");
      // Pitch in scientific notation; pitchToVexKey will lowercase it
      const pitch = midiToPitchName(tonicMidi + n.interval, useFlats);
      current.chords.push({
        keys: [pitch],
        duration: dur,
      });
      beats += n.beats;
      if (firstLineNote && pendingLabel) {
        current.label = pendingLabel;
        pendingLabel = undefined;
        firstLineNote = false;
      }
      if (beats >= beatsPerBar - 1e-9) {
        measures.push(current);
        current = { chords: [] };
        beats = 0;
      }
    }
  }
  if (current.chords.length) measures.push(current);
  return measures;
}

/* ─────────────────────────────────────────────────────────────────────
 *  Repertoire — encoded ONCE, transposed to any key.
 *  Intervals are semitones above the tonic, and we use values >7 freely
 *  to express the actual octave the melody sits in (no mod-12 wrap).
 *  Beats are quarter-note units.
 *  ───────────────────────────────────────────────────────────────────── */

export const TWINKLE_MELODY: MelodyLine[] = [
  {
    label: "1 · Twinkle, twinkle, little star",
    notes: [
      { interval: 0,  beats: 1 }, { interval: 0,  beats: 1 }, // Twin-kle
      { interval: 7,  beats: 1 }, { interval: 7,  beats: 1 }, // twin-kle
      { interval: 9,  beats: 1 }, { interval: 9,  beats: 1 }, // lit-tle
      { interval: 7,  beats: 2 },                              // star…
    ],
  },
  {
    label: "2 · How I wonder what you are",
    notes: [
      { interval: 5,  beats: 1 }, { interval: 5,  beats: 1 }, // How I
      { interval: 4,  beats: 1 }, { interval: 4,  beats: 1 }, // won-der
      { interval: 2,  beats: 1 }, { interval: 2,  beats: 1 }, // what you
      { interval: 0,  beats: 2 },                              // are…
    ],
  },
  {
    label: "3 · Up above the world so high",
    notes: [
      { interval: 7,  beats: 1 }, { interval: 7,  beats: 1 }, // Up a-
      { interval: 5,  beats: 1 }, { interval: 5,  beats: 1 }, // bove the
      { interval: 4,  beats: 1 }, { interval: 4,  beats: 1 }, // world so
      { interval: 2,  beats: 2 },                              // high…
    ],
  },
  {
    label: "4 · Like a diamond in the sky",
    notes: [
      { interval: 7,  beats: 1 }, { interval: 7,  beats: 1 },
      { interval: 5,  beats: 1 }, { interval: 5,  beats: 1 },
      { interval: 4,  beats: 1 }, { interval: 4,  beats: 1 },
      { interval: 2,  beats: 2 },
    ],
  },
  {
    label: "5 · Twinkle, twinkle, little star",
    notes: [
      { interval: 0,  beats: 1 }, { interval: 0,  beats: 1 },
      { interval: 7,  beats: 1 }, { interval: 7,  beats: 1 },
      { interval: 9,  beats: 1 }, { interval: 9,  beats: 1 },
      { interval: 7,  beats: 2 },
    ],
  },
  {
    label: "6 · How I wonder what you are",
    notes: [
      { interval: 5,  beats: 1 }, { interval: 5,  beats: 1 },
      { interval: 4,  beats: 1 }, { interval: 4,  beats: 1 },
      { interval: 2,  beats: 1 }, { interval: 2,  beats: 1 },
      { interval: 0,  beats: 2 },
    ],
  },
];

/** Flatten a list of lines into one continuous melody. */
export function flatten(lines: MelodyLine[]): MelodyNote[] {
  return lines.flatMap((l) => l.notes);
}

/**
 * Pick a sensible tonic MIDI for a pitch class so the melody sits around middle
 * C and stays mostly within the treble stave.
 *
 * We take the SHORTEST path from C4 — up to F♯, then down — rather than always
 * transposing upward. Two reasons: keys from F♯ to B used to push the tune to
 * A5 and above, off the top of the stave; and `<Notation transposeFromTonicPc>`
 * already picks the shortest direction, so always-up would put a printed score
 * and its playback an octave apart the first time a lesson used both.
 */
export function tonicMidiForPc(tonicPc: number): number {
  const pc = ((tonicPc % 12) + 12) % 12;
  return 60 + (pc > 6 ? pc - 12 : pc);
}
